import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { FOCUS_RING } from '@/shared/components/pillClasses';
import type { TaskLabelView } from '@/lib/api/generated/model';

import { labelSlotClass } from '../labelSlots';
import { LABEL_CHIP_SHAPE, type LabelChipDensity } from '../chipShape';

export interface RemovableLabelChipProps {
  label: Pick<TaskLabelView, 'id' | 'name' | 'color'>;
  /**
   * Narrow the surrounding surface to this label.
   *
   * Its presence is what decides the chip's *shape*, not just an extra
   * handler — see the two modes below.
   */
  onNarrow?: (labelId: string) => void;
  /** Take this label off the task, or out of the filter. */
  /**
   * Absent for someone who may only read the workspace (spec 189): the chip then
   * keeps its narrowing, or is just the label, with no `×`.
   */
  onRemove?: (labelId: string) => void;
  density?: LabelChipDensity;
}

/**
 * A chip that can be acted on, with its remove control inside the pill.
 *
 * The `×` used to sit outside, in muted grey, separated by a negative margin.
 * That made a label look like two things standing near each other rather than
 * one object with a way to dismiss it — and the grey `×` read as belonging to
 * the row, not to the label, so on a row of several it was never obvious which
 * one a given `×` would remove. Inside the pill, wearing the pill's own colour,
 * it can only be read one way.
 *
 * **Two shapes, chosen by whether narrowing means anything here.**
 *
 * *With* `onNarrow` — the drawer — the pill holds two sibling buttons: the name
 * narrows, the `×` removes. They stay separate because "show me more like this"
 * and "I did not mean this label" are opposite intentions, and folding them
 * into one target makes an ordinary click ambiguous. Siblings, never nested: a
 * remove control inside a narrowing button would be a button inside a button —
 * invalid, and in practice a click that fires both.
 *
 * *Without* it — the filter row — the whole pill is one button that removes.
 * Every chip in that row already *is* the current narrowing, so there is no
 * narrowing left to apply; the name used to be dead text beside a live `×`,
 * which meant the one gesture a reader actually tries, pressing the label they
 * want gone, did nothing at all. One target, one meaning.
 */
export function RemovableLabelChip({
  label,
  onNarrow,
  onRemove,
  density = 'card',
}: RemovableLabelChipProps) {
  // Asymmetric: the name keeps a full inset, the `×` sits closer to the edge so
  // the pill does not grow a lopsided empty margin around it.
  const shape = cn(
    LABEL_CHIP_SHAPE[density],
    'gap-0.5 py-0 pr-0.5 pl-2.5',
    labelSlotClass(label.color),
  );

  // Inherits the chip's own foreground rather than the muted role it used to
  // wear: inside a tinted pill, muted grey is a colour from a different
  // surface. The hover fill is drawn from the foreground already in use here,
  // so it works on all eight tints and in both themes without knowing which.
  const dismissGlyph = 'rounded-full p-0.5 opacity-70 transition-colors';

  if (!onRemove) {
    const plain = cn(
      LABEL_CHIP_SHAPE[density],
      'px-2.5',
      labelSlotClass(label.color),
    );
    return onNarrow ? (
      <button
        type="button"
        data-testid="label-chip"
        onClick={() => {
          onNarrow(label.id);
        }}
        aria-label={`Show all tasks labelled ${label.name}`}
        className={cn(plain, 'transition-opacity hover:opacity-70', FOCUS_RING)}
      >
        {label.name}
      </button>
    ) : (
      <span data-testid="label-chip" className={plain}>
        {label.name}
      </span>
    );
  }

  if (!onNarrow) {
    return (
      <button
        type="button"
        data-testid="label-chip"
        onClick={() => {
          onRemove(label.id);
        }}
        aria-label={`Remove label ${label.name}`}
        className={cn(shape, 'hover:opacity-80', FOCUS_RING)}
      >
        {label.name}
        {/*
          Decoration, not a second target. The button around it already carries
          the whole meaning, so a nested control here would be the button-inside-
          a-button this component exists to avoid.
        */}
        <span aria-hidden="true" className={dismissGlyph}>
          <XIcon className="size-3" />
        </span>
      </button>
    );
  }

  return (
    <span data-testid="label-chip" className={shape}>
      <button
        type="button"
        onClick={() => {
          onNarrow(label.id);
        }}
        // Named beyond the label's own word: a bare "acme" gives a screen
        // reader no idea that pressing it filters rather than opens.
        aria-label={`Show all tasks labelled ${label.name}`}
        className={cn(
          'rounded-4xl transition-opacity hover:opacity-70',
          FOCUS_RING,
        )}
      >
        {label.name}
      </button>
      <button
        type="button"
        onClick={() => {
          onRemove(label.id);
        }}
        aria-label={`Remove label ${label.name}`}
        className={cn(
          dismissGlyph,
          'hover:bg-foreground/15 hover:opacity-100',
          FOCUS_RING,
        )}
      >
        <XIcon aria-hidden="true" className="size-3" />
      </button>
    </span>
  );
}
